import { useEffect, useMemo, useState } from "react";
import { formatUzPhone, initialPhoneInput, isBlankPhoneInput } from "../phone";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { Archive, Building2, MapPin, Pencil, Phone, Plus, Users } from "lucide-react";
import { usePageHeader } from "../lib/use-page-header";
import { api, type BranchOut } from "../api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { FeatureGate } from "../components/feature-gate";
import { useTariff } from "../lib/use-tariff";

interface Props {
  onOpenTariff: () => void;
}

function archiveErr(code: string | undefined, t: TFunction): string | null {
  switch (code) {
    case "branch_last_active":
      return t("branches.errLastActive");
    case "branch_has_active_employees":
      return t("branches.errActiveEmployees");
    default:
      return null;
  }
}

export function BranchesPage({ onOpenTariff }: Props) {
  return (
    <FeatureGate feature="branches" requiredTariff="pro" onUpgrade={onOpenTariff}>
      <BranchesInner onOpenTariff={onOpenTariff} />
    </FeatureGate>
  );
}

function BranchesInner({ onOpenTariff }: Props) {
  const { t } = useTranslation(["settings", "common"]);
  const [items, setItems] = useState<BranchOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<BranchOut | "new" | null>(null);
  const [confirm, setConfirm] = useState<BranchOut | null>(null);
  const [refresh, setRefresh] = useState(0);
  const { status, reload: reloadTariff } = useTariff();

  useEffect(() => {
    setLoading(true);
    api
      .branches()
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [refresh]);

  async function del(id: number) {
    try {
      const res = await api.deleteBranch(id);
      if (res.warning === "branch_has_stock") {
        toast.warning(t("branches.archivedWithStock"));
      } else {
        toast.success(t("branches.archivedToast"));
      }
      setRefresh((x) => x + 1);
      reloadTariff();
    } catch (e) {
      toast.error(archiveErr((e as { code?: string }).code, t) || (e as { message?: string }).message || t("branches.error"));
    } finally {
      setConfirm(null);
    }
  }

  const limits = status?.limits;
  const limitReached =
    limits?.max_branches != null && limits.current_branches >= limits.max_branches;

  const actionsNode = useMemo(
    () => (
      <Button
        size="sm"
        onClick={() => setEditing("new")}
        disabled={limitReached}
      >
        <Plus />
        <span className="hidden sm:inline">{t("branches.newBranch")}</span>
      </Button>
    ),
    [limitReached, t],
  );

  usePageHeader({ actions: actionsNode });

  return (
    <div className="space-y-4">
      {limitReached && (
        <div className="rounded-md border border-warning/30 bg-warning/10 text-warning px-4 py-3 text-sm flex items-center justify-between gap-3 flex-wrap">
          <span>
            {t("branches.limitBanner", {
              current: limits?.current_branches,
              max: limits?.max_branches,
            })}
          </span>
          <Button size="sm" variant="outline" onClick={onOpenTariff}>
            {t("branches.upgradeTariff")}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Building2 />}
              title={t("branches.empty.title")}
              description={t("branches.empty.desc")}
              action={
                <Button onClick={() => setEditing("new")}>
                  <Plus />
                  {t("branches.newBranch")}
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((b) => (
            <Card key={b.id} className={b.is_active ? undefined : "opacity-60"}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Building2 className="size-5" />
                  </div>
                  {b.is_active ? (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditing(b)}
                        aria-label={t("branches.edit")}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setConfirm(b)}
                        aria-label={t("branches.archive")}
                      >
                        <Archive />
                      </Button>
                    </div>
                  ) : (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {t("branches.archived")}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-lg leading-tight">
                    {b.name}
                  </h3>
                  {b.address && (
                    <p className="text-sm text-muted-foreground flex items-start gap-1.5 mt-1.5">
                      <MapPin className="size-3.5 mt-0.5 shrink-0" />
                      <span>{b.address}</span>
                    </p>
                  )}
                  {b.telefon && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                      <Phone className="size-3.5 shrink-0" />
                      <span>{formatUzPhone(b.telefon)}</span>
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                    <Users className="size-3.5 shrink-0" />
                    <span>{t("branches.activeMasters", { count: b.faol_ustalar })}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <BranchForm
            item={editing === "new" ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              setRefresh((x) => x + 1);
              reloadTariff();
            }}
          />
        )}
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        {confirm && (
          <>
            <DialogHeader>
              <DialogTitle>{t("branches.confirm.title")}</DialogTitle>
              <DialogDescription>
                {t("branches.confirm.desc", { name: confirm.name })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirm(null)}>
                {t("common:actions.cancel")}
              </Button>
              <Button variant="destructive" onClick={() => del(confirm.id)}>
                {t("branches.archive")}
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </div>
  );
}

function BranchForm({
  item,
  onCancel,
  onSaved,
}: {
  item: BranchOut | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["settings", "common"]);
  const [name, setName] = useState(item?.name ?? "");
  const [address, setAddress] = useState(item?.address ?? "");
  const [telefon, setTelefon] = useState(initialPhoneInput(item?.telefon));
  // Ommaviy profil — ustaxona profili (SettingsPage) bilan bir xil maydonlar.
  const [lat, setLat] = useState(item?.latitude != null ? String(item.latitude) : "");
  const [lng, setLng] = useState(item?.longitude != null ? String(item.longitude) : "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [workHours, setWorkHours] = useState(item?.work_hours ?? "");
  const [instagram, setInstagram] = useState(item?.instagram ?? "");
  const [telegram, setTelegram] = useState(item?.telegram ?? "");
  const [website, setWebsite] = useState(item?.website ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Google Maps havolasidan koordinatalarni ajratib oladi (@lat,lng yoki ?q=lat,lng).
  const parseMapsLink = (value: string) => {
    const m =
      value.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/) ||
      value.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (m) {
      setLat(m[1]);
      setLng(m[2]);
      toast.success(t("general.profile.mapsLinkParsed"));
    } else {
      toast.error(t("general.profile.mapsLinkInvalid"));
    }
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      name,
      address: address.trim() || null,
      telefon: isBlankPhoneInput(telefon) ? null : telefon.trim(),
      latitude: lat.trim() === "" ? null : Number(lat),
      longitude: lng.trim() === "" ? null : Number(lng),
      description: description.trim() || null,
      work_hours: workHours.trim() || null,
      instagram: instagram.trim() || null,
      telegram: telegram.trim() || null,
      website: website.trim() || null,
    };
    try {
      if (item) {
        await api.updateBranch(item.id, payload);
      } else {
        await api.createBranch(payload);
      }
      toast.success(item ? t("branches.form.updated") : t("branches.form.added"));
      onSaved();
    } catch (e) {
      const er = e as { code?: string; message?: string };
      setErr(
        er.code === "location_incomplete"
          ? t("general.profile.locationIncomplete")
          : er.message || t("branches.form.error"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{item ? t("branches.form.editTitle") : t("branches.form.newTitle")}</DialogTitle>
        <DialogDescription>{t("branches.form.desc")}</DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="b-name">{t("branches.form.nameLabel")}</Label>
          <Input
            id="b-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("branches.form.namePlaceholder")}
            required
            autoFocus
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="b-addr">{t("branches.form.addressLabel")}</Label>
          <Input
            id="b-addr"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={t("branches.form.addressPlaceholder")}
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="b-tel">{t("branches.form.phoneLabel")}</Label>
          <Input
            id="b-tel"
            value={telefon}
            onChange={(e) => setTelefon(e.target.value)}
            placeholder={t("branches.form.phonePlaceholder")}
            disabled={busy}
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t("general.profile.location")}</Label>
          <Input
            placeholder={t("general.profile.mapsLinkPlaceholder")}
            onChange={(e) => {
              const v = e.target.value.trim();
              if (v) parseMapsLink(v);
            }}
            disabled={busy}
          />
          <div className="flex gap-2 flex-wrap">
            <Input
              inputMode="decimal"
              placeholder={t("general.profile.lat")}
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              className="max-w-[10rem]"
              disabled={busy}
            />
            <Input
              inputMode="decimal"
              placeholder={t("general.profile.lng")}
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              className="max-w-[10rem]"
              disabled={busy}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="b-desc">{t("general.profile.description")}</Label>
          <Textarea
            id="b-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            disabled={busy}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="b-hours">{t("general.profile.workHours")}</Label>
          <Input
            id="b-hours"
            value={workHours}
            onChange={(e) => setWorkHours(e.target.value)}
            placeholder={t("general.profile.workHoursPlaceholder")}
            disabled={busy}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="b-ig">{t("general.profile.instagram")}</Label>
            <Input
              id="b-ig"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@..."
              disabled={busy}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="b-tg">{t("general.profile.telegram")}</Label>
            <Input
              id="b-tg"
              value={telegram}
              onChange={(e) => setTelegram(e.target.value)}
              placeholder="@..."
              disabled={busy}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="b-web">{t("general.profile.website")}</Label>
            <Input
              id="b-web"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://..."
              disabled={busy}
            />
          </div>
        </div>

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
            {err}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("common:actions.cancel")}
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? t("common:actions.saving") : t("common:actions.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}
